/* eslint-disable camelcase */
// DEPRECATED. Use selectNoteDraft or selectReplyDraft, you scrub.
import { createSelector } from 'reselect'

import { reverseFindIndex, reverseForEach, emptyArr } from 'util/arrays'
import { strftime } from 'util/date'

import { selectDeviceTypeIsMobile } from 'selectors/app'
import { selectCurrentMailbox } from 'selectors/mailboxes/selectCurrentMailbox'
import { selectCurrentTicket } from 'selectors/tickets/current/selectCurrentTicket'
import { selectCurrentTicketRawChangesets } from 'selectors/currentChangesets'
import { selectReplyDraft, selectDraftIsForwarded } from './replyDrafts'
import { selectReplyRecipient } from './recipients'

// use agent name if mailbox is missing (to prevent crash) and when the preference
// to use agent name is set
const useAgentName = mailbox => !mailbox || mailbox.from_name === 'Agent'

const getAuthor = (mailbox, message, actor) => {
  const { from, agent_response } = message
  if (!agent_response) return from

  return {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    name: useAgentName(mailbox) ? actor.name : mailbox.name,
    email: mailbox ? mailbox.email : '',
  }
}

const getTimestamp = (message, mailbox) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const time = useAgentName(mailbox)
    ? message.createdAtInAgentTimezone
    : message.createdAtInAccountTimezone
  return strftime('%b %Do, 20%y at %l:%M %p', time)
}

const replyByline = (mailbox, message) => {
  const change = message.change
  const timestamp = getTimestamp(change, mailbox)
  const { name, email } = getAuthor(mailbox, change, message.actor)

  if (name || email) {
    return `<div>On ${timestamp}, ${name || ''} ${
      email ? `<${email}>` : ''
    } wrote:</div>`
  }
  return `<div>On ${timestamp}:</div>`
}

const quotedText = (mailbox, messages) => {
  let html = ''

  messages.forEach(message => {
    html = html.concat(`
      ${replyByline(mailbox, message)}
      <blockquote>${message.change.body}
    `)
  })

  return html.concat('</blockquote>'.repeat(messages.length))
}

const decorateUserAsMailbox = (user, mailbox) => {
  const newUser = Object.assign({}, user)
  if (mailbox) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    newUser.name = useAgentName(mailbox) ? user.name : mailbox.name
    newUser.email = mailbox.email
  }
  return newUser
}

const quotedForwardText = (mailbox, messages, ticket) => {
  if (messages.length === 0) return null
  let html = ''
  const first = messages[0]
  const firstChange = first.change
  const rest = messages.slice(1, messages.length)
  let author = first.actor
  let recipient = firstChange.to
  if (first.change.agent_response) {
    author = decorateUserAsMailbox(author, mailbox)
  } else {
    recipient = decorateUserAsMailbox(recipient, mailbox)
  }

  html += '---- Forwarded Message ----<br>'
  html += '<br>'
  html += `From: ${author.name}${
    author.email ? ` &lt;${author.email}&gt;` : ''
  }<br>`
  html += `Date: ${getTimestamp(firstChange)}<br>`
  html += `Subject: ${ticket.title}<br>`
  html += `To: ${recipient.name}${
    recipient.email ? ` &lt;${recipient.email}&gt;` : ''
  }<br><br>`
  html += firstChange.body
  html += '<br>'
  html += '<br>'

  rest.forEach(message => {
    html = html.concat(`
      ${replyByline(mailbox, message)}
      <blockquote>${message.change.body}
    `)
  })

  return html.concat('</blockquote>'.repeat(rest.length))
}

const selectTicketDraftQuotedText = createSelector(
  selectReplyDraft,
  draft => (draft ? draft.quotedText : undefined)
)

const selectStartCommentId = () => {
  const pattern = /\/tickets\/\d+\/forward\/(\d+)/
  const pathname = window.location.pathname
  const matches = pathname.match(pattern)
  return matches ? matches[1] : null
}

const selectCurrentMessages = createSelector(
  selectCurrentTicketRawChangesets,
  changesets => {
    const actions = []
    if (!changesets) return emptyArr

    changesets.forEach(changeset => {
      changeset.actions.forEach(action => {
        if (action.change_type === 'Message') actions.push(action)
      })
    })

    return actions
  }
)

const selectConversationForForward = createSelector(
  selectCurrentMessages,
  selectStartCommentId,
  (messages, commentId) => {
    const maxLength = 3
    const filteredMessages = []

    // of commentId is present, filter down the messages
    // to start with that comment
    if (commentId) {
      let shouldAdd = false
      // eslint-disable-next-line consistent-return
      reverseForEach(messages, message => {
        // we hit the requested comment, add it and all following
        if (message.change.id === commentId) shouldAdd = true
        if (shouldAdd && !message.change.forward) filteredMessages.push(message)
        if (filteredMessages.length >= maxLength) return false // stop searching
      })
    }

    return filteredMessages
  }
)

const selectConversationWithReplyRecipient = createSelector(
  selectCurrentMessages,
  selectReplyRecipient,
  (messages, recipient) => {
    const maxLength = 3
    const isRecipient = user => user && user.id === recipient.id
    const isWithRecipient = ({ from, to }) =>
      isRecipient(from) || isRecipient(to)

    const conversation = []
    let conversationType // value determined by first message we find
    reverseFindIndex(messages, messageAction => {
      const message = messageAction.change
      if (conversation.length === maxLength) return true

      if (isWithRecipient(message)) {
        if (!conversationType)
          conversationType = message.isWithCollaborator
            ? 'Collaborator'
            : 'Customer'
        // only include messages with specific user if recipient is collaborator
        if (message.isWithCollaborator) conversation.push(messageAction)
      }

      // return conversation with all customers on ticket if recipient is customer
      if (conversationType === 'Customer' && message.isWithCustomer)
        conversation.push(messageAction)

      return false
    })

    return conversation
  }
)

export const selectQuotedText = createSelector(
  selectCurrentMailbox,
  selectConversationWithReplyRecipient,
  selectConversationForForward,
  selectTicketDraftQuotedText,
  selectCurrentTicket,
  selectDraftIsForwarded,
  selectDeviceTypeIsMobile,
  (
    mailbox,
    messages,
    forwardMessages,
    draftQuotedText,
    ticket,
    isForwarding,
    isMobile
  ) => {
    if (typeof draftQuotedText === 'string') {
      return draftQuotedText
    }
    if (isForwarding && isMobile) {
      return quotedForwardText(mailbox, forwardMessages, ticket)
    } else if (isForwarding) {
      // NOTE (jscheel): Fall back on legacy forward message generation in api
      // for desktop forwarding.
      return null
    }
    return quotedText(mailbox, messages)
  }
)

export const selectShouldIncludeQuotedText = createSelector(
  selectTicketDraftQuotedText,
  draftQuotedText => {
    return draftQuotedText === undefined || draftQuotedText === null
  }
)
